import { useAuth0 } from '@auth0/auth0-react';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Link,
    Paper,
    Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLogger } from '../../hooks/useLogger';
import { clearLocalSession } from '../../utils/session';

const SUPPORT_EMAIL = 'support@hustro.com';

export const RestrictedAccessPage = () => {
    const intl = useIntl();
    const logger = useLogger();
    const { user, logout } = useAuth0();

    useEffect(() => {
        logger.error(
            `Restricted simple form access denied for user: ${user?.email ?? 'unknown'}`,
        );
    }, [logger, user?.email]);

    const handleLogout = () => {
        // Preserve the current form id so RedirectToSimpleFormId (mounted on `/`) can
        // route the user back to the same form after Auth0 returns to the app origin.
        // returnTo is intentionally left to Auth0's default (application origin) — a
        // specific /form/<id> path is not in the Allowed Logout URLs whitelist.
        clearLocalSession();
        logout();
    };

    return (
        <Box
            sx={{
                boxSizing: 'border-box',
                minHeight: {
                    xs: 'calc(100vh - 56px)',
                    sm: 'calc(100vh - 64px)',
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 3, sm: 5 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'warning.light',
                            color: 'warning.contrastText',
                        }}
                    >
                        <LockPersonOutlinedIcon sx={{ fontSize: 40 }} />
                    </Box>

                    <Typography
                        variant="h5"
                        component="h1"
                        align="center"
                        fontWeight={600}
                    >
                        {intl.formatMessage({
                            id: 'restricted_form_no_access_title',
                        })}
                    </Typography>

                    <Typography
                        variant="body1"
                        color="text.secondary"
                        align="center"
                    >
                        {intl.formatMessage({
                            id: 'restricted_form_no_access_message',
                        })}
                    </Typography>

                    {user?.email && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {intl.formatMessage({
                                    id: 'restricted_form_signed_in_as',
                                })}
                            </Typography>
                            <Chip
                                label={user.email}
                                variant="outlined"
                                sx={{ maxWidth: '100%' }}
                            />
                        </Box>
                    )}

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                    >
                        {intl.formatMessage({
                            id: 'restricted_form_no_access_hint',
                        })}
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<LogoutOutlinedIcon />}
                        onClick={handleLogout}
                        sx={{ mt: 1 }}
                    >
                        {intl.formatMessage({
                            id: 'restricted_form_logout_and_use_other_account',
                        })}
                    </Button>

                    <Divider flexItem sx={{ mt: 1 }} />

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                    >
                        {intl.formatMessage(
                            { id: 'restricted_form_support' },
                            {
                                supportLink: (
                                    <Link
                                        href={`mailto:${SUPPORT_EMAIL}`}
                                        underline="hover"
                                    >
                                        {SUPPORT_EMAIL}
                                    </Link>
                                ),
                            },
                        )}
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};
